import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useAuth } from '../lib/AuthContext';
import { useOutlet } from '../lib/OutletContext';
import {
  getDashboardSnapshot, getDashboardAlerts, getTodayMovements,
  getIngredients, getInventoryByCategory, getTopMovers, getProcurementSummary,
  globalSync, getSyncStatus, forceRetrySource, getUnresolved, resolveUnresolved,
  getUnresolvedGrouped, ignoreUnresolved, mapUnresolvedToIngredient, searchIngredients,
  flagUnitIssue,
  getSalesCaptureSyncStatus, triggerSalesCaptureSync, getSalesCaptureUnresolved,
  getSalesCaptureSyncRuns, getSalesCaptureUnmappedImpact,
} from '../lib/api';
import StatCard from '../components/StatCard';
import AlertCard from '../components/AlertCard';
import {
  Package, TrendingUp, AlertTriangle, Activity, RefreshCw, X,
  ArrowUpRight, ArrowDownRight, BarChart3, ShieldAlert, Layers,
  ShoppingBag, ChevronDown, ChevronUp, Info,
} from 'lucide-react';
import { formatCurrency, formatDateTime } from '../lib/utils';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import logger from '../lib/logger';
import MissingCostDrawer from '../components/MissingCostDrawer';

const POLL_INTERVAL = 15000;
const BACKGROUND_SYNC_INTERVAL = 5 * 60 * 1000; // 5 minutes

/* ── Sync v2 — truth-surface helpers ─────────────────────────── */
const SOURCE_LABELS = {
  control_room: 'Control Room',
  vendor_engine: 'Expense Capture (retired)',
  trials: 'Trials',
};
const SOURCE_PRIORITY = { control_room: 0, vendor_engine: 1, trials: 2 };

const getTimeSince = (isoStr) => {
  if (!isoStr) return null;
  const diff = (Date.now() - new Date(isoStr).getTime()) / 1000;
  if (diff < 0) return 'just now';
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

const getTimeUntil = (isoStr) => {
  if (!isoStr) return null;
  const diff = (new Date(isoStr).getTime() - Date.now()) / 1000;
  if (diff <= 0) return null;
  if (diff < 60) return `${Math.ceil(diff)}s`;
  return `${Math.ceil(diff / 60)}m`;
};

const TECH_HEALTH_DOT = {
  HEALTHY: 'bg-emerald-500',
  DEGRADED: 'bg-yellow-500',
  DOWN: 'bg-red-500',
};
const DATA_HEALTH_BADGE = {
  PARTIAL: { bg: 'bg-yellow-500/10', text: 'text-yellow-500', border: 'border-yellow-500/30' },
  BLOCKED: { bg: 'bg-red-500/10', text: 'text-red-500', border: 'border-red-500/30' },
};

/* ── Single sync source row (v2 truth row) ───────────────────── */
const SyncSourceRow = ({ sourceKey, src, isSyncing, onForceRetry }) => {
  const [retrying, setRetrying] = useState(false);

  const techHealth = src.technical_health || 'HEALTHY';
  const dataHealth = src.data_health || 'CLEAN';
  const committedAt = src.last_committed_at;
  const attemptAt = src.last_attempt_at;
  const batchSize = src.last_batch_size || 0;
  const applied = src.last_applied_count || 0;
  const skipped = src.last_skipped_count || 0;
  const unresolvedCount = src.last_unresolved_count || 0;
  const cooldown = src.cooldown_remaining_seconds || 0;
  const lastError = src.last_error;
  const nextRetry = src.next_retry_after;
  const isRetired = Boolean(src.retired) || techHealth === 'RETIRED';
  const isDegraded = !isRetired && (techHealth === 'DEGRADED' || techHealth === 'DOWN');
  const retryIn = nextRetry ? getTimeUntil(nextRetry) : null;
  const dotColor = isRetired ? 'bg-zinc-600' : (TECH_HEALTH_DOT[techHealth] || 'bg-zinc-600');
  const dhBadge = isRetired ? null : DATA_HEALTH_BADGE[dataHealth];

  const handleRetry = async () => {
    if (retrying || isSyncing) return;
    setRetrying(true);
    try { await onForceRetry(sourceKey); }
    finally { setRetrying(false); }
  };

  return (
    <div className="py-2 border-b border-[#1f1f23] last:border-0" data-testid={`sync-source-${sourceKey}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 min-w-0">
          <div className={`w-2 h-2 rounded-full shrink-0 ${dotColor} ${isSyncing ? 'animate-pulse' : ''}`} />
          <span className="text-sm text-zinc-200 font-medium truncate">{src.label || sourceKey}</span>
          {isRetired ? (
            <span className="text-[10px] font-mono shrink-0 text-zinc-500" data-testid={`sync-source-retired-${sourceKey}`}>
              RETIRED · purchases now from Bills
            </span>
          ) : (
            <span className={`text-[10px] font-mono shrink-0 ${
              techHealth === 'HEALTHY' ? 'text-emerald-600' : techHealth === 'DEGRADED' ? 'text-yellow-600' : 'text-red-600'
            }`}>{techHealth}</span>
          )}
          {dhBadge && (
            <span className={`text-[10px] px-1.5 py-0.5 rounded border shrink-0 ${dhBadge.bg} ${dhBadge.text} ${dhBadge.border}`}>
              {dataHealth === 'PARTIAL' ? `${unresolvedCount} unresolved` : 'BLOCKED'}
            </span>
          )}
        </div>
        <span className="text-[11px] text-zinc-600 font-mono shrink-0 ml-2">
          {committedAt ? getTimeSince(committedAt) : 'never'}
        </span>
      </div>

      {batchSize > 0 && (
        <div className="flex gap-3 mt-1 ml-4 text-[10px] text-zinc-600">
          <span>Applied: <span className="text-emerald-500 font-mono">{applied}</span></span>
          <span>Skipped: <span className="text-zinc-400 font-mono">{skipped}</span></span>
          <span>Batch: <span className="font-mono">{batchSize}</span></span>
        </div>
      )}

      {attemptAt && committedAt && attemptAt > committedAt && (
        <p className="mt-0.5 ml-4 text-[10px] text-zinc-600">
          Last attempt: {getTimeSince(attemptAt)}
          {src.avg_response_time_ms > 0 ? ` (${src.avg_response_time_ms}ms)` : ''}
        </p>
      )}

      {lastError && isDegraded && (
        <p className="mt-0.5 ml-4 text-[10px] text-red-500/80 truncate" title={lastError}>{lastError}</p>
      )}

      {retryIn && isDegraded && (
        <p className="mt-0.5 ml-4 text-[10px] text-zinc-600">Next retry in {retryIn}</p>
      )}

      {!isDegraded && cooldown > 0 && (
        <p className="mt-0.5 ml-4 text-[10px] text-zinc-600">Next sync in {cooldown}s</p>
      )}

      {isDegraded && !isSyncing && (
        <div className="mt-1.5 ml-4">
          <button
            onClick={handleRetry}
            disabled={retrying}
            className="text-[10px] px-2.5 py-1 bg-amber-600/10 text-amber-400 border border-amber-500/30 rounded hover:bg-amber-600/20 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5"
            data-testid={`force-retry-${sourceKey}`}
          >
            <RefreshCw className={`w-3 h-3 ${retrying ? 'animate-spin' : ''}`} />
            {retrying ? 'Retrying...' : 'Force Retry'}
          </button>
        </div>
      )}
    </div>
  );
};

/* ── Sync Status Panel v2 (truth surface) ────────────────────── */
const SyncStatusPanel = ({ syncStatus, isSyncing, onForceRetry, onViewUnresolved }) => {
  if (!syncStatus?.sources) return null;

  const entries = Object.entries(syncStatus.sources);
  const canonical = entries.filter(([, s]) => s.type === 'canonical')
    .sort((a, b) => (SOURCE_PRIORITY[a[0]] ?? 99) - (SOURCE_PRIORITY[b[0]] ?? 99));
  const auxiliary = entries.filter(([, s]) => s.type !== 'canonical');
  const unresolvedTotal = syncStatus.unresolved_queue_count || 0;
  const overallTech = syncStatus.overall_technical_health || 'HEALTHY';
  const overallData = syncStatus.overall_data_health || 'CLEAN';

  return (
    <div className="bg-[#18181b] border border-[#27272a] rounded-xl p-4" data-testid="sync-status-panel">
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-sm font-semibold text-zinc-300">Data Sources</h3>
        <div className="flex items-center gap-2">
          {isSyncing && <span className="text-[10px] text-emerald-400 animate-pulse">Syncing...</span>}
          {unresolvedTotal > 0 && (
            <button
              onClick={onViewUnresolved}
              className="text-[10px] px-2 py-0.5 bg-yellow-500/10 text-yellow-400 border border-yellow-500/30 rounded hover:bg-yellow-500/20 transition-colors"
              data-testid="view-unresolved-btn"
            >
              {unresolvedTotal} unresolved
            </button>
          )}
        </div>
      </div>

      <div className="flex gap-2 mb-3 text-[10px]">
        <span className={`px-1.5 py-0.5 rounded ${
          overallTech === 'HEALTHY' ? 'bg-emerald-500/10 text-emerald-500' :
          overallTech === 'DEGRADED' ? 'bg-yellow-500/10 text-yellow-500' :
          'bg-red-500/10 text-red-500'
        }`}>{overallTech}</span>
        <span className={`px-1.5 py-0.5 rounded ${
          overallData === 'CLEAN' ? 'bg-emerald-500/10 text-emerald-500' :
          overallData === 'PARTIAL' ? 'bg-yellow-500/10 text-yellow-500' :
          'bg-red-500/10 text-red-500'
        }`}>{overallData}</span>
      </div>

      {canonical.length > 0 && (
        <div className="mb-2">
          <p className="text-[10px] uppercase tracking-widest text-zinc-600 mb-1 font-semibold">Canonical</p>
          {canonical.map(([key, src]) => (
            <SyncSourceRow key={key} sourceKey={key} src={src} isSyncing={isSyncing} onForceRetry={onForceRetry} />
          ))}
        </div>
      )}

      {auxiliary.length > 0 && (
        <div>
          <p className="text-[10px] uppercase tracking-widest text-zinc-600 mb-1 font-semibold">Auxiliary</p>
          {auxiliary.map(([key, src]) => (
            <SyncSourceRow key={key} sourceKey={key} src={src} isSyncing={isSyncing} onForceRetry={onForceRetry} />
          ))}
        </div>
      )}
    </div>
  );
};

/* ── Shared modal ────────────────────────────────────────────── */
const DrillDownModal = ({ isOpen, onClose, title, children }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-[#18181b] border border-[#27272a] rounded-xl w-full max-w-4xl max-h-[80vh] overflow-hidden shadow-2xl">
        <div className="flex items-center justify-between p-4 border-b border-[#27272a]">
          <h2 className="text-xl font-bold">{title}</h2>
          <button onClick={onClose} className="p-2 hover:bg-[#27272a] rounded-lg transition-colors" data-testid="modal-close-btn">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6 overflow-y-auto max-h-[calc(80vh-80px)]">{children}</div>
      </div>
    </div>
  );
};

/* ── Category bar ────────────────────────────────────────────── */
const CAT_COLORS = { FOOD: '#10b981', LIQUOR: '#8b5cf6', BEVERAGE: '#3b82f6' };

const CategoryBar = ({ categories, total }) => {
  if (total === 0) return <div className="h-3 bg-[#27272a] rounded-full" />;
  return (
    <div className="flex h-3 rounded-full overflow-hidden gap-0.5">
      {categories.filter(c => c.value > 0).map(c => (
        <div
          key={c.code}
          style={{ width: `${c.pct}%`, backgroundColor: CAT_COLORS[c.code] }}
          className="rounded-full transition-all"
          title={`${c.code}: ${formatCurrency(c.value)}`}
        />
      ))}
    </div>
  );
};

/* ── Main dashboard ──────────────────────────────────────────── */
const Dashboard = () => {
  const { user } = useAuth();
  const { selectedOutlet } = useOutlet();
  const navigate = useNavigate();

  const [snapshot, setSnapshot] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [movements, setMovements] = useState(null);
  const [categoryData, setCategoryData] = useState(null);
  const [topMovers, setTopMovers] = useState(null);
  const [procSummary, setProcSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isPolling, setIsPolling] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const pollIntervalRef = useRef(null);
  const bgSyncRef = useRef(null);

  const [unresolvedItems, setUnresolvedItems] = useState([]);
  const [unresolvedModalOpen, setUnresolvedModalOpen] = useState(false);
  const [unresolvedLoading, setUnresolvedLoading] = useState(false);
  const [missingCostDrawerOpen, setMissingCostDrawerOpen] = useState(false);
  const [unresolvedGrouped, setUnresolvedGrouped] = useState(null);
  const [unresolvedTab, setUnresolvedTab] = useState('all');
  const [expandedItem, setExpandedItem] = useState(null);
  const [mappingItem, setMappingItem] = useState(null);
  const [ingredientSearch, setIngredientSearch] = useState('');
  const [ingredientResults, setIngredientResults] = useState([]);
  const [selectedIngredient, setSelectedIngredient] = useState(null);
  const [mappingSubmitting, setMappingSubmitting] = useState(false);

  // Drill-down states
  const [inventoryModalOpen, setInventoryModalOpen] = useState(false);
  const [lowStockModalOpen, setLowStockModalOpen] = useState(false);
  const [movementsModalOpen, setMovementsModalOpen] = useState(false);
  const [ingredientsData, setIngredientsData] = useState([]);
  const [drillDownLoading, setDrillDownLoading] = useState(false);

  // Sales Capture states
  const [salesCaptureStatus, setSalesCaptureStatus] = useState(null);
  const [salesCaptureSyncing, setSalesCaptureSyncing] = useState(false);
  const [salesCaptureUnresolved, setSalesCaptureUnresolved] = useState([]);
  const [salesCaptureUnresolvedOpen, setSalesCaptureUnresolvedOpen] = useState(false);
  const [salesCaptureSyncRuns, setSalesCaptureSyncRuns] = useState([]);
  const [salesCaptureRunsOpen, setSalesCaptureRunsOpen] = useState(false);
  const [salesCaptureUnmappedImpact, setSalesCaptureUnmappedImpact] = useState([]);
  const [salesCaptureUnmappedOpen, setSalesCaptureUnmappedOpen] = useState(false);
  const [salesCaptureDateRange, setSalesCaptureDateRange] = useState({
    start_date: new Date().toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0],
  });

  const loadDashboardData = useCallback(async (showToast = false) => {
    if (!selectedOutlet) { setLoading(false); return; }
    try {
      const [snap, alertsData, movData, catData, movers, proc, syncSt] = await Promise.all([
        getDashboardSnapshot(selectedOutlet),
        getDashboardAlerts(selectedOutlet),
        getTodayMovements(selectedOutlet),
        getInventoryByCategory(selectedOutlet).catch(() => null),
        getTopMovers(selectedOutlet).catch(() => null),
        getProcurementSummary(selectedOutlet).catch(() => null),
        getSyncStatus().catch(() => null),
      ]);
      setSnapshot(snap);
      setAlerts(
        Array.isArray(alertsData)
          ? alertsData
          : (alertsData?.alerts || [])
      );
      setMovements(movData);
      setCategoryData(catData);
      setTopMovers(movers);
      setProcSummary(proc);
      setSyncStatus(syncSt);
      setLastUpdated(new Date());
      // Load Sales Capture status in parallel
      getSalesCaptureSyncStatus(selectedOutlet).then(sc => setSalesCaptureStatus(sc)).catch(() => {});
      if (showToast) toast.success('Data refreshed');
    } catch (error) {
      if (showToast) toast.error('Failed to refresh data');
      logger.error('[Dashboard] Error:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedOutlet]);

  const loadIngredientsForDrillDown = async () => {
    if (!selectedOutlet) return;
    setDrillDownLoading(true);
    try {
      const data = await getIngredients(selectedOutlet);
      setIngredientsData(data.ingredients || []);
    } catch (error) {
      logger.error('[Dashboard] Error loading ingredients:', error);
      toast.error('Failed to load ingredients data');
    } finally {
      setDrillDownLoading(false);
    }
  };

  const handleCardClick = (type) => {
    if (type === 'inventory' || type === 'lowStock') {
      loadIngredientsForDrillDown();
    }
    if (type === 'inventory') setInventoryModalOpen(true);
    else if (type === 'lowStock') setLowStockModalOpen(true);
    else if (type === 'movements') setMovementsModalOpen(true);
  };

  useEffect(() => { loadDashboardData(); }, [loadDashboardData]);

  const handleGlobalSync = async (isForce = true) => {
    setIsSyncing(true);
    try {
      const syncResult = await globalSync(isForce);
      const r = syncResult?.results || {};
      const errors = syncResult?.errors || {};

      // Detect if everything was cooldown/skipped (no actual sync happened)
      const resultEntries = Object.entries(r);
      const allSkipped = resultEntries.length > 0 && resultEntries.every(([, s]) =>
        s.status === 'cooldown' || s.status === 'skipped'
      );
      if (allSkipped) {
        toast.info('All sources on cooldown — no sync needed');
        await loadDashboardData(false);
        return;
      }

      const parts = [];
      const warnings = [];
      const failures = [];

      for (const [key, src] of resultEntries) {
        const label = SOURCE_LABELS[key] || key;
        if (src.status === 'success') {
          if (src.applied > 0) parts.push(`${label}: ${src.applied} applied`);
          if (src.unresolved > 0) warnings.push(`${label}: ${src.unresolved} unresolved`);
          if (src.queue_resolved > 0) parts.push(`${label}: ${src.queue_resolved} queue resolved`);
        } else if (src.status === 'failed') {
          failures.push(`${label}: ${errors[key] || 'failed'}`);
        } else if (src.status === 'blocked') {
          failures.push(`${label}: blocked — ${src.reason || 'dependency'}`);
        } else if (src.status === 'locked') {
          warnings.push(`${label}: locked`);
        }
      }

      if (syncResult?.status === 'failed') {
        toast.error(`Sync failed: ${failures.join(', ')}`, { duration: 6000 });
      } else if (failures.length > 0) {
        toast.warning(`Partial sync: ${[...failures, ...warnings].join(', ')}`, { duration: 6000 });
      } else if (warnings.length > 0) {
        toast.warning(`Sync: ${warnings.join(', ')}`, { duration: 6000 });
      } else if (parts.length > 0) {
        toast.success(`Synced: ${parts.join(', ')}`);
      } else {
        toast.success('All sources up to date — no new data');
      }

      await loadDashboardData(false);
    } catch (err) {
      toast.error('Sync failed: ' + (err?.response?.data?.detail || err.message));
    } finally {
      setIsSyncing(false);
    }
  };

  // Background sync loop (5 min)
  useEffect(() => {
    bgSyncRef.current = setInterval(() => {
      globalSync(false).then(r => {
        if (r?.status !== 'cooldown') {
          loadDashboardData(false);
          logger.info('[Dashboard] Background sync:', r?.status);
        }
      }).catch(() => {});
    }, BACKGROUND_SYNC_INTERVAL);
    return () => { if (bgSyncRef.current) clearInterval(bgSyncRef.current); };
  }, [loadDashboardData]);

  const handleForceRetry = async (sourceKey) => {
    try {
      const result = await forceRetrySource(sourceKey);
      if (result?.status === 'rate_limited') {
        toast.info(result.message || 'Force retry rate limited — wait 30s');
        return;
      }
      const sourceResult = result?.results?.[sourceKey];
      const label = SOURCE_LABELS[sourceKey] || sourceKey;
      if (sourceResult?.status === 'success') {
        toast.success(`${label}: ${sourceResult.applied || 0} applied, ${sourceResult.skipped || 0} skipped`);
      } else if (sourceResult?.status === 'locked') {
        toast.info(`${label}: locked by another sync`);
      } else if (sourceResult?.status === 'failed') {
        toast.error(`${label}: ${result?.errors?.[sourceKey] || 'retry failed'}`);
      } else if (sourceResult?.status === 'blocked') {
        toast.warning(`${label}: blocked — ${sourceResult.reason || 'dependency failure'}`);
      } else {
        toast.success(`${label}: retry completed`);
      }
      await loadDashboardData(false);
    } catch (err) {
      toast.error(`Force retry failed: ${err?.response?.data?.detail || err.message}`);
    }
  };

  const loadUnresolved = async () => {
    setUnresolvedLoading(true);
    try {
      const [data, grouped] = await Promise.all([
        getUnresolved(),
        getUnresolvedGrouped(),
      ]);
      setUnresolvedItems(data.items || []);
      setUnresolvedGrouped(grouped);
    } catch (err) {
      logger.error('[Dashboard] Error loading unresolved:', err);
      toast.error('Failed to load unresolved items');
    } finally {
      setUnresolvedLoading(false);
    }
  };

  const handleDismissItem = async (queueId) => {
    if (!window.confirm('This will dismiss the item without fixing the root cause. The underlying data issue still exists upstream. Continue?')) return;
    try {
      await resolveUnresolved(queueId);
      toast.success('Item dismissed (not fixed)');
      await loadUnresolved();
      await loadDashboardData(false);
    } catch (err) {
      toast.error('Failed to dismiss: ' + (err?.response?.data?.detail || err.message));
    }
  };

  const handleIgnoreItem = async (queueId) => {
    try {
      await ignoreUnresolved(queueId);
      toast.success('Marked as non-consumable');
      await loadUnresolved();
      await loadDashboardData(false);
    } catch (err) {
      toast.error('Failed to ignore: ' + (err?.response?.data?.detail || err.message));
    }
  };

  const handleIngredientSearch = async (query) => {
    setIngredientSearch(query);
    setSelectedIngredient(null);
    if (query.length < 2) { setIngredientResults([]); return; }
    try {
      const data = await searchIngredients(query);
      setIngredientResults(data.items || []);
    } catch { setIngredientResults([]); }
  };

  const handleSubmitMapping = async () => {
    if (!mappingItem || !selectedIngredient) return;
    setMappingSubmitting(true);
    try {
      const result = await mapUnresolvedToIngredient(
        mappingItem.queue_id,
        selectedIngredient.ingredient_id,
        selectedIngredient.ingredient_name,
      );
      if (result.upstream_accepted) {
        toast.success('Mapping sent to Expense Capture. Will apply on next sync.');
      } else {
        toast.success('Mapping saved. Expense Capture endpoint pending — will retry.');
      }
      setMappingItem(null);
      setSelectedIngredient(null);
      setIngredientSearch('');
      setIngredientResults([]);
      await loadUnresolved();
    } catch (err) {
      toast.error('Mapping failed: ' + (err?.response?.data?.detail || err.message));
    } finally {
      setMappingSubmitting(false);
    }
  };

  const handleFlagUnitIssue = async (queueId) => {
    try {
      await flagUnitIssue(queueId);
      toast.success('Unit issue flagged for upstream fix');
      await loadUnresolved();
    } catch (err) {
      toast.error('Failed to flag: ' + (err?.response?.data?.detail || err.message));
    }
  };

  useEffect(() => {
    if (isPolling && selectedOutlet) {
      pollIntervalRef.current = setInterval(() => loadDashboardData(false), POLL_INTERVAL);
    }
    return () => { if (pollIntervalRef.current) clearInterval(pollIntervalRef.current); };
  }, [isPolling, selectedOutlet, loadDashboardData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="dashboard-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-emerald-500" />
      </div>
    );
  }

  const lowStockItems = ingredientsData.filter(i => i.risk_flag === 'HIGH' || i.soh <= 0);
  const topValuedItems = [...ingredientsData]
    .map(i => ({ ...i, value: (i.soh || 0) * (i.avg_cost || 0) }))
    .sort((a, b) => b.value - a.value)
    .slice(0, 10);

  return (
    <div className="space-y-6" data-testid="dashboard-root">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Dashboard</h1>
          {lastUpdated && (
            <p className="text-xs text-zinc-500 mt-1">
              Last updated {formatDateTime(lastUpdated.toISOString())}
            </p>
          )}
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => handleGlobalSync(true)}
            disabled={isSyncing}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              isSyncing ? 'bg-emerald-500/10 text-emerald-500 border border-emerald-500/30 cursor-wait' : 'bg-emerald-600 hover:bg-emerald-700 text-white'
            }`}
            data-testid="global-sync-btn"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${isSyncing ? 'animate-spin' : ''}`} />
            {isSyncing ? 'Syncing...' : 'Sync All'}
          </button>
          <button
            onClick={() => setIsPolling(!isPolling)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              isPolling ? 'bg-emerald-500/10 text-emerald-500 border border-emerald-500/30' : 'bg-[#27272a] text-zinc-400 hover:text-white'
            }`}
            data-testid="auto-refresh-toggle"
          >
            {isPolling ? 'Live' : 'Auto-refresh'}
          </button>
        </div>
      </div>

      {/* Cost-coverage banner — surfaces the difference between
          "₹0 because no stock" and "₹0 because cost data hasn't populated yet".
          We never fabricate valuation; we explain it. */}
      {(() => {
        const cov = snapshot?.cost_coverage;
        if (!cov) return null;
        const missing = cov.items_missing_cost || 0;
        const withStock = cov.items_with_stock || 0;
        const withCost = cov.items_with_cost || 0;
        const totalValue = snapshot?.total_inventory_value || 0;

        // Case A: stock exists, but valuation is fully unavailable.
        if (totalValue === 0 && withStock > 0) {
          return (
            <button
              type="button"
              onClick={() => setMissingCostDrawerOpen(true)}
              className="w-full text-left rounded-lg border border-amber-500/30 bg-amber-500/5 hover:bg-amber-500/10 transition-colors p-3 flex items-start gap-3"
              data-testid="inventory-valuation-unavailable-banner"
            >
              <Info className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1 text-sm">
                <p className="text-amber-200 font-medium">
                  Inventory quantity exists, but valuation is unavailable because cost data has not populated yet.
                </p>
                <p className="text-amber-300/70 text-xs mt-1">
                  {withStock} ingredient{withStock !== 1 ? 's' : ''} have stock; cost will populate as priced purchases arrive from Bills.
                </p>
                {cov.sample_missing_cost_names?.length > 0 && (
                  <p className="text-amber-300/60 text-xs mt-1 font-mono">
                    e.g. {cov.sample_missing_cost_names.join(', ')}
                    {missing > cov.sample_missing_cost_names.length && ` +${missing - cov.sample_missing_cost_names.length} more`}
                  </p>
                )}
                <p className="text-amber-300 text-xs mt-1.5 font-medium underline-offset-2 underline decoration-amber-400/40">
                  Review missing-cost ingredients →
                </p>
              </div>
            </button>
          );
        }

        // Case B: partial — some items priced, some not.
        if (missing > 0 && withCost > 0) {
          return (
            <button
              type="button"
              onClick={() => setMissingCostDrawerOpen(true)}
              className="w-full text-left rounded-lg border border-amber-500/30 bg-amber-500/5 hover:bg-amber-500/10 transition-colors p-3 flex items-start gap-3"
              data-testid="inventory-partial-cost-banner"
            >
              <Info className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1 text-sm">
                <p className="text-amber-200">
                  <strong data-testid="inventory-partial-cost-count">{missing}</strong>{' '}
                  ingredient{missing !== 1 ? 's' : ''} have stock but missing cost — they're excluded from the total value above.
                </p>
                {cov.sample_missing_cost_names?.length > 0 && (
                  <p className="text-amber-300/60 text-xs mt-1 font-mono">
                    e.g. {cov.sample_missing_cost_names.join(', ')}
                    {missing > cov.sample_missing_cost_names.length && ` +${missing - cov.sample_missing_cost_names.length} more`}
                  </p>
                )}
                <p className="text-amber-300 text-xs mt-1.5 font-medium underline-offset-2 underline decoration-amber-400/40">
                  Review missing-cost ingredients →
                </p>
              </div>
            </button>
          );
        }
        return null;
      })()}

      {/* KPI Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="kpi-row">
        <div onClick={() => handleCardClick('inventory')} className="cursor-pointer" data-testid="kpi-inventory-value">
          <StatCard
            title="Total Inventory Value"
            value={formatCurrency(snapshot?.total_inventory_value || 0)}
            icon={Package}
            subtitle={
              <div className="flex items-center gap-2 flex-wrap">
                <span>Click for breakdown</span>
              </div>
            }
          />
        </div>
        <div onClick={() => handleCardClick('lowStock')} className="cursor-pointer" data-testid="kpi-low-stock">
          <StatCard
            title="Low Stock Items"
            value={snapshot?.low_stock_count || 0}
            icon={AlertTriangle}
            subtitle="Click to review"
          />
        </div>
        <div onClick={() => handleCardClick('movements')} className="cursor-pointer" data-testid="kpi-movements">
          <StatCard
            title="Today's Movements"
            value={movements?.summary?.total_movements || 0}
            icon={Activity}
            subtitle="View details"
          />
        </div>
        <div data-testid="kpi-alerts">
          <StatCard
            title="Active Alerts"
            value={alerts?.length || 0}
            icon={ShieldAlert}
          />
        </div>
      </div>

      {/* Category Breakdown + Top Movers + Sync Status Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Category Breakdown */}
        <div className="bg-[#18181b] border border-[#27272a] rounded-xl p-5" data-testid="category-breakdown-panel">
          <div className="flex items-center gap-2 mb-4">
            <Layers className="w-4 h-4 text-zinc-400" />
            <h3 className="text-sm font-semibold text-zinc-300 uppercase tracking-wider">Inventory by Category</h3>
          </div>
          {categoryData ? (
            <div className="space-y-4">
              <CategoryBar categories={categoryData.categories} total={categoryData.total_value} />
              <div className="space-y-3">
                {categoryData.categories.map(cat => (
                  <div key={cat.code} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: CAT_COLORS[cat.code] }} />
                      <span className="text-sm text-zinc-300">{cat.code}</span>
                      <span className="text-xs text-zinc-500">{cat.items} items</span>
                    </div>
                    <div className="text-right">
                      <span className="text-sm font-mono font-semibold">{formatCurrency(cat.value)}</span>
                      <span className="text-xs text-zinc-500 ml-2">{cat.pct}%</span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="pt-3 border-t border-[#27272a] flex justify-between">
                <span className="text-sm text-zinc-400">Total</span>
                <span className="text-sm font-mono font-bold text-emerald-500">{formatCurrency(categoryData.total_value)}</span>
              </div>
            </div>
          ) : (
            <p className="text-sm text-zinc-500">Loading...</p>
          )}
        </div>

        {/* Top Movers */}
        <div className="bg-[#18181b] border border-[#27272a] rounded-xl p-5" data-testid="top-movers-panel">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="w-4 h-4 text-zinc-400" />
            <h3 className="text-sm font-semibold text-zinc-300 uppercase tracking-wider">Top Items by Value</h3>
          </div>
          {topMovers?.by_value?.length > 0 ? (
            <div className="space-y-2">
              {topMovers.by_value.slice(0, 6).map((item, idx) => (
                <div
                  key={item.ingredient_id}
                  className="flex items-center justify-between p-2.5 bg-[#09090b] border border-[#27272a] rounded-lg hover:border-emerald-500/20 transition-colors cursor-pointer"
                  onClick={() => navigate('/ingredients')}
                  data-testid={`top-mover-${idx}`}
                >
                  <div className="flex items-center gap-2.5">
                    <span className="text-xs text-zinc-500 w-5 font-mono">#{idx + 1}</span>
                    <div>
                      <p className="text-sm font-medium leading-tight">{item.ingredient_name}</p>
                      <p className="text-xs text-zinc-500">
                        {item.soh} {item.base_unit} &middot;
                        <span className="ml-1" style={{ color: CAT_COLORS[item.category] }}>{item.category}</span>
                      </p>
                    </div>
                  </div>
                  <span className="text-sm font-mono font-semibold text-emerald-500">{formatCurrency(item.value)}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-zinc-500">No stock data available</p>
          )}
        </div>

        {/* Sync Status Panel */}
        <SyncStatusPanel
          syncStatus={syncStatus}
          isSyncing={isSyncing}
          onForceRetry={handleForceRetry}
          onViewUnresolved={() => { loadUnresolved(); setUnresolvedModalOpen(true); }}
        />

        {/* Sales Capture Panel */}
        <div className="bg-[#18181b] border border-[#27272a] rounded-xl p-5" data-testid="sales-capture-panel">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <ShoppingBag className="w-4 h-4 text-zinc-400" />
              <h3 className="text-sm font-semibold text-zinc-300 uppercase tracking-wider">Sales Capture</h3>
              {/* Mode badge */}
              {salesCaptureStatus?.sync_mode_current && (
                <span className={`px-1.5 py-0.5 text-[10px] font-mono font-medium rounded ${
                  salesCaptureStatus.sync_mode_current === 'active' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' :
                  salesCaptureStatus.sync_mode_current === 'monitor' ? 'bg-blue-500/10 text-blue-400 border border-blue-500/20' :
                  'bg-amber-500/10 text-amber-400 border border-amber-500/20'
                }`} data-testid="sales-capture-mode-badge">
                  {salesCaptureStatus.sync_mode_current.toUpperCase()}
                </span>
              )}
              {/* Activation blocked badge */}
              {salesCaptureStatus?.activation_blocked && (
                <span className="px-1.5 py-0.5 text-[10px] font-mono font-medium bg-red-500/10 text-red-400 border border-red-500/20 rounded" data-testid="sales-capture-blocked-badge">
                  BLOCKED
                </span>
              )}
            </div>
            {salesCaptureStatus && salesCaptureStatus.status !== 'never_synced' && (
              <span className={`px-2 py-0.5 text-[10px] font-mono font-medium rounded ${
                salesCaptureStatus.sync_health === 'HEALTHY' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' :
                salesCaptureStatus.sync_health === 'PARTIAL' ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20' :
                salesCaptureStatus.sync_health === 'DEGRADED' ? 'bg-orange-500/10 text-orange-400 border border-orange-500/20' :
                salesCaptureStatus.sync_health === 'BLOCKED' ? 'bg-red-500/10 text-red-400 border border-red-500/20' :
                'bg-zinc-500/10 text-zinc-400 border border-zinc-500/20'
              }`} data-testid="sales-capture-health">
                {salesCaptureStatus.sync_health || 'UNKNOWN'}
              </span>
            )}
          </div>

          {salesCaptureStatus?.configured ? (
            <div className="space-y-3">
              {/* Coverage bar (show after first sync) */}
              {salesCaptureStatus.status !== 'never_synced' && salesCaptureStatus.mapping_coverage !== undefined && (
                <div data-testid="sales-capture-coverage-section">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[10px] text-zinc-500 uppercase">Mapping Coverage</span>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs font-mono font-bold ${
                        salesCaptureStatus.mapping_coverage >= (salesCaptureStatus.coverage_threshold || 85) ? 'text-emerald-400' :
                        salesCaptureStatus.mapping_coverage >= 70 ? 'text-amber-400' : 'text-red-400'
                      }`} data-testid="sales-capture-coverage-pct">
                        {salesCaptureStatus.mapping_coverage?.toFixed(1)}%
                      </span>
                      <span className="text-[10px] text-zinc-600">/ {salesCaptureStatus.coverage_threshold || 85}% req</span>
                    </div>
                  </div>
                  <div className="w-full h-2 bg-[#09090b] rounded-full overflow-hidden border border-[#27272a]">
                    <div
                      className={`h-full rounded-full transition-all duration-500 ${
                        salesCaptureStatus.mapping_coverage >= (salesCaptureStatus.coverage_threshold || 85) ? 'bg-emerald-500' :
                        salesCaptureStatus.mapping_coverage >= 70 ? 'bg-amber-500' : 'bg-red-500'
                      }`}
                      style={{ width: `${Math.min(salesCaptureStatus.mapping_coverage || 0, 100)}%` }}
                      data-testid="sales-capture-coverage-bar"
                    />
                  </div>
                  {/* Threshold marker */}
                  {salesCaptureStatus.activation_blocked && (
                    <p className="text-[10px] text-red-400/80 mt-1 bg-red-500/5 border border-red-500/10 rounded px-2 py-1" data-testid="sales-capture-block-reason">
                      Active mode blocked: coverage {salesCaptureStatus.mapping_coverage?.toFixed(1)}% is below {salesCaptureStatus.coverage_threshold}% threshold. Resolve unmapped items to enable.
                    </p>
                  )}
                </div>
              )}

              {/* Metrics row */}
              {salesCaptureStatus.status !== 'never_synced' && (
                <div className="grid grid-cols-4 gap-2">
                  <div className="p-2 bg-[#09090b] border border-[#27272a] rounded-lg text-center">
                    <p className="text-lg font-mono font-bold text-zinc-200">{salesCaptureStatus.fetched_rows ?? 0}</p>
                    <p className="text-[10px] text-zinc-500">Fetched</p>
                  </div>
                  <div className="p-2 bg-[#09090b] border border-[#27272a] rounded-lg text-center">
                    <p className="text-lg font-mono font-bold text-emerald-400">{salesCaptureStatus.mapped_rows ?? 0}</p>
                    <p className="text-[10px] text-zinc-500">Mapped</p>
                  </div>
                  <div className="p-2 bg-[#09090b] border border-[#27272a] rounded-lg text-center">
                    <p className="text-lg font-mono font-bold text-amber-400">{salesCaptureStatus.unresolved_rows ?? 0}</p>
                    <p className="text-[10px] text-zinc-500">Unresolved</p>
                  </div>
                  <div className="p-2 bg-[#09090b] border border-[#27272a] rounded-lg text-center">
                    <p className="text-lg font-mono font-bold text-blue-400">
                      {salesCaptureStatus.sync_mode_current === 'active' && !salesCaptureStatus.activation_blocked
                        ? salesCaptureStatus.depletion_events_written ?? 0
                        : salesCaptureStatus.theoretical_depletion_events ?? salesCaptureStatus.depletion_events_written ?? 0}
                    </p>
                    <p className="text-[10px] text-zinc-500">
                      {salesCaptureStatus.sync_mode_current === 'active' && !salesCaptureStatus.activation_blocked ? 'Depletion' : 'Theoretical'}
                    </p>
                  </div>
                </div>
              )}

              {/* Confidence */}
              {salesCaptureStatus.confidence && (
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-zinc-500 uppercase">Confidence:</span>
                  <span className={`px-1.5 py-0.5 text-[10px] font-mono font-medium rounded ${
                    salesCaptureStatus.confidence === 'HIGH_CONFIDENCE' ? 'bg-emerald-500/10 text-emerald-400' :
                    salesCaptureStatus.confidence === 'PARTIAL_CONFIDENCE' ? 'bg-amber-500/10 text-amber-400' :
                    salesCaptureStatus.confidence === 'LOW_CONFIDENCE' ? 'bg-orange-500/10 text-orange-400' :
                    'bg-red-500/10 text-red-400'
                  }`} data-testid="sales-capture-confidence">
                    {salesCaptureStatus.confidence?.replace('_', ' ')}
                  </span>
                </div>
              )}

              {/* Last sync info */}
              {salesCaptureStatus.last_sync_at && (
                <p className="text-[10px] text-zinc-500">
                  Last sync: {formatDateTime(salesCaptureStatus.last_sync_at)} ({salesCaptureStatus.last_start_date} → {salesCaptureStatus.last_end_date})
                </p>
              )}

              {/* Error */}
              {salesCaptureStatus.error_summary && (
                <p className="text-[10px] text-red-400/80 bg-red-500/5 border border-red-500/10 rounded px-2 py-1 truncate">
                  {salesCaptureStatus.error_summary}
                </p>
              )}

              {/* Sync trigger */}
              <div className="flex items-end gap-2 pt-2 border-t border-[#27272a]">
                <div className="flex-1 grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-[10px] text-zinc-500 block mb-1">Start</label>
                    <input
                      type="date"
                      value={salesCaptureDateRange.start_date}
                      onChange={e => setSalesCaptureDateRange(prev => ({...prev, start_date: e.target.value}))}
                      className="w-full px-2 py-1 text-xs bg-[#09090b] border border-[#27272a] rounded text-zinc-300"
                      data-testid="sales-capture-start-date"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] text-zinc-500 block mb-1">End</label>
                    <input
                      type="date"
                      value={salesCaptureDateRange.end_date}
                      onChange={e => setSalesCaptureDateRange(prev => ({...prev, end_date: e.target.value}))}
                      className="w-full px-2 py-1 text-xs bg-[#09090b] border border-[#27272a] rounded text-zinc-300"
                      data-testid="sales-capture-end-date"
                    />
                  </div>
                </div>
                <button
                  onClick={async () => {
                    setSalesCaptureSyncing(true);
                    try {
                      const report = await triggerSalesCaptureSync({
                        outlet_id: selectedOutlet,
                        ...salesCaptureDateRange,
                      });
                      setSalesCaptureStatus(prev => ({...prev, ...report}));
                      if (report.activation_blocked) {
                        toast.error(`Active mode blocked: coverage ${report.mapping_coverage?.toFixed(1)}% < ${report.coverage_threshold}% threshold`);
                      } else {
                        toast.success(`Sales sync: ${report.mapped_rows} mapped, ${report.unresolved_rows} unresolved (${report.mapping_coverage?.toFixed(1)}% coverage)`);
                      }
                    } catch (e) {
                      toast.error(`Sales sync failed: ${e.message}`);
                    } finally {
                      setSalesCaptureSyncing(false);
                      getSalesCaptureSyncStatus(selectedOutlet).then(sc => setSalesCaptureStatus(sc)).catch(() => {});
                    }
                  }}
                  disabled={salesCaptureSyncing}
                  className="px-3 py-1.5 text-xs font-medium bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded transition-colors"
                  data-testid="sales-capture-sync-btn"
                >
                  {salesCaptureSyncing ? 'Syncing...' : 'Sync Sales'}
                </button>
              </div>

              {/* Action links */}
              <div className="flex items-center gap-3 text-[10px]">
                <button
                  onClick={async () => {
                    const data = await getSalesCaptureUnresolved(selectedOutlet);
                    setSalesCaptureUnresolved(data.items || []);
                    setSalesCaptureUnresolvedOpen(true);
                  }}
                  className="text-amber-400 hover:text-amber-300 underline underline-offset-2"
                  data-testid="sales-capture-view-unresolved"
                >
                  View Unresolved ({salesCaptureStatus.unresolved_rows ?? 0})
                </button>
                <button
                  onClick={async () => {
                    const data = await getSalesCaptureUnmappedImpact(selectedOutlet);
                    setSalesCaptureUnmappedImpact(data.items || []);
                    setSalesCaptureUnmappedOpen(true);
                  }}
                  className="text-red-400 hover:text-red-300 underline underline-offset-2"
                  data-testid="sales-capture-view-unmapped-impact"
                >
                  Top Unmapped Impact
                </button>
                <button
                  onClick={async () => {
                    const data = await getSalesCaptureSyncRuns(selectedOutlet, 5);
                    setSalesCaptureSyncRuns(data.runs || []);
                    setSalesCaptureRunsOpen(true);
                  }}
                  className="text-zinc-400 hover:text-zinc-300 underline underline-offset-2"
                  data-testid="sales-capture-view-runs"
                >
                  Sync History
                </button>
              </div>
            </div>
          ) : (
            <p className="text-xs text-zinc-500">Sales Capture Engine not configured</p>
          )}
        </div>
      </div>

      {/* Procurement + Alerts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Procurement Summary */}
        <div className="bg-[#18181b] border border-[#27272a] rounded-xl p-5" data-testid="procurement-summary-panel">
          <div className="flex items-center gap-2 mb-4">
            <BarChart3 className="w-4 h-4 text-zinc-400" />
            <h3 className="text-sm font-semibold text-zinc-300 uppercase tracking-wider">Procurement</h3>
          </div>
          {procSummary ? (
            <div className="space-y-4">
              {/* Recent bills */}
              <div>
                <p className="text-xs text-zinc-500 mb-2">Recent Bills Ingested</p>
                {procSummary.recent_bills?.length > 0 ? (
                  <div className="space-y-1.5">
                    {procSummary.recent_bills.map((bill, i) => (
                      <div key={bill._id || `bill-${i}`} className="flex items-center justify-between p-2.5 bg-[#09090b] border border-[#27272a] rounded-lg">
                        <div>
                          <p className="text-xs text-zinc-400">{bill._id?.slice(0, 12)}...</p>
                          <p className="text-xs text-zinc-500">{bill.line_count} lines</p>
                        </div>
                        <span className="text-sm font-mono font-semibold text-emerald-500">{formatCurrency(bill.total_value || 0)}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-zinc-500">No bills ingested yet</p>
                )}
              </div>
              {/* Duplicate risk */}
              <div className="pt-3 border-t border-[#27272a]">
                <p className="text-xs text-zinc-500 mb-2">Duplicate Risk</p>
                <div className="grid grid-cols-3 gap-2">
                  <div className="text-center p-2 bg-[#09090b] border border-amber-500/20 rounded-lg">
                    <p className="text-lg font-mono font-bold text-amber-500">{procSummary.duplicate_risk?.held || 0}</p>
                    <p className="text-xs text-zinc-500">Held</p>
                  </div>
                  <div className="text-center p-2 bg-[#09090b] border border-[#27272a] rounded-lg">
                    <p className="text-lg font-mono font-bold text-zinc-400">{procSummary.duplicate_risk?.skipped || 0}</p>
                    <p className="text-xs text-zinc-500">Skipped</p>
                  </div>
                  <div className="text-center p-2 bg-[#09090b] border border-emerald-500/20 rounded-lg">
                    <p className="text-lg font-mono font-bold text-emerald-500">{procSummary.duplicate_risk?.overridden || 0}</p>
                    <p className="text-xs text-zinc-500">Override</p>
                  </div>
                </div>
              </div>
              {/* Forecast accuracy */}
              {procSummary.forecast_accuracy?.total_forecasts_with_actuals > 0 ? (
                <div className="pt-3 border-t border-[#27272a]">
                  <p className="text-xs text-zinc-500 mb-1">Forecast Accuracy (14d)</p>
                  <p className="text-2xl font-mono font-bold text-emerald-500">
                    {(procSummary.forecast_accuracy.overall_accuracy_rate * 100).toFixed(0)}%
                  </p>
                </div>
              ) : (
                <div className="pt-3 border-t border-[#27272a]">
                  <p className="text-xs text-zinc-500">Forecast accuracy will appear once consumption data flows in</p>
                </div>
              )}
            </div>
          ) : (
            <p className="text-sm text-zinc-500">Loading...</p>
          )}
        </div>

        {/* Active Alerts */}
        <div className="bg-[#18181b] border border-[#27272a] rounded-xl p-5" data-testid="alerts-panel">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="w-4 h-4 text-zinc-400" />
            <h3 className="text-sm font-semibold text-zinc-300 uppercase tracking-wider">Active Alerts</h3>
          </div>
          {alerts.length === 0 ? (
            <p className="text-sm text-zinc-500 py-4">No active alerts</p>
          ) : (
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {alerts.map((alert, i) => (
                <AlertCard key={alert.alert_id || i} alert={alert} />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Drill-Down Modals ─────────────────────────────────── */}

      {/* Inventory Value Drill-Down */}
      <DrillDownModal
        isOpen={inventoryModalOpen}
        onClose={() => setInventoryModalOpen(false)}
        title="Inventory Value Breakdown"
      >
        {drillDownLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-emerald-500" />
          </div>
        ) : (
          <div className="space-y-6">
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-[#09090b] border border-[#27272a] rounded-lg p-4">
                <p className="text-xs text-zinc-400 mb-1">Total Value</p>
                <p className="text-2xl font-bold font-mono text-emerald-500">
                  {formatCurrency(snapshot?.total_inventory_value || 0)}
                </p>
              </div>
              <div className="bg-[#09090b] border border-[#27272a] rounded-lg p-4">
                <p className="text-xs text-zinc-400 mb-1">Unique SKUs</p>
                <p className="text-2xl font-bold font-mono">{ingredientsData.length}</p>
              </div>
              <div className="bg-[#09090b] border border-[#27272a] rounded-lg p-4">
                <p className="text-xs text-zinc-400 mb-1">Items with Stock</p>
                <p className="text-2xl font-bold font-mono">{ingredientsData.filter(i => i.soh > 0).length}</p>
              </div>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-zinc-400 uppercase tracking-wider mb-3">Top 10 Items by Value</h3>
              <div className="space-y-2">
                {topValuedItems.map((item, idx) => (
                  <div
                    key={item.ingredient_id}
                    className="flex items-center justify-between p-3 bg-[#09090b] border border-[#27272a] rounded-lg hover:border-emerald-500/30 transition-colors cursor-pointer"
                    onClick={() => { setInventoryModalOpen(false); navigate('/ingredients'); }}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-zinc-500 w-6">#{idx + 1}</span>
                      <div>
                        <p className="text-sm font-medium">{item.ingredient_name}</p>
                        <p className="text-xs text-zinc-500">{item.soh} {item.base_unit} x {formatCurrency(item.avg_cost || 0)}/{item.base_unit}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-mono font-semibold text-emerald-500">{formatCurrency(item.value || 0)}</p>
                      <p className="text-xs text-zinc-500">{((item.value / (snapshot?.total_inventory_value || 1)) * 100).toFixed(1)}%</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <button
              onClick={() => { setInventoryModalOpen(false); navigate('/ingredients'); }}
              className="w-full py-3 bg-emerald-600 hover:bg-emerald-700 rounded-lg font-medium transition-colors"
            >
              View All Ingredients
            </button>
          </div>
        )}
      </DrillDownModal>

      {/* Low Stock Drill-Down */}
      <DrillDownModal
        isOpen={lowStockModalOpen}
        onClose={() => setLowStockModalOpen(false)}
        title="Low Stock Items"
      >
        {drillDownLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-amber-500" />
          </div>
        ) : (
          <div className="space-y-6">
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-[#09090b] border border-red-500/30 rounded-lg p-4">
                <p className="text-xs text-zinc-400 mb-1">Out of Stock</p>
                <p className="text-2xl font-bold font-mono text-red-500">{ingredientsData.filter(i => i.soh <= 0).length}</p>
              </div>
              <div className="bg-[#09090b] border border-amber-500/30 rounded-lg p-4">
                <p className="text-xs text-zinc-400 mb-1">Low Stock</p>
                <p className="text-2xl font-bold font-mono text-amber-500">{ingredientsData.filter(i => i.risk_flag === 'HIGH' && i.soh > 0).length}</p>
              </div>
              <div className="bg-[#09090b] border border-[#27272a] rounded-lg p-4">
                <p className="text-xs text-zinc-400 mb-1">Total Flagged</p>
                <p className="text-2xl font-bold font-mono">{lowStockItems.length}</p>
              </div>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-zinc-400 uppercase tracking-wider mb-3">Items Requiring Attention</h3>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {lowStockItems.length === 0 ? (
                  <p className="text-sm text-zinc-500 text-center py-8">No low stock items</p>
                ) : (
                  lowStockItems.map(item => (
                    <div
                      key={item.ingredient_id}
                      className={`flex items-center justify-between p-3 bg-[#09090b] border rounded-lg transition-colors cursor-pointer ${
                        item.soh <= 0 ? 'border-red-500/50 hover:border-red-500' : 'border-amber-500/30 hover:border-amber-500'
                      }`}
                      onClick={() => { setLowStockModalOpen(false); navigate('/ingredients'); }}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-2 h-2 rounded-full ${item.soh <= 0 ? 'bg-red-500' : 'bg-amber-500'}`} />
                        <div>
                          <p className="text-sm font-medium">{item.ingredient_name}</p>
                          <p className="text-xs text-zinc-500">{item.category || 'Uncategorized'}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={`text-sm font-mono font-semibold ${item.soh <= 0 ? 'text-red-500' : 'text-amber-500'}`}>
                          {item.soh} {item.base_unit}
                        </p>
                        <p className="text-xs text-zinc-500">{item.soh <= 0 ? 'OUT OF STOCK' : 'LOW STOCK'}</p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
            <button
              onClick={() => { setLowStockModalOpen(false); navigate('/ingredients'); }}
              className="w-full py-3 bg-amber-600 hover:bg-amber-700 rounded-lg font-medium transition-colors"
            >
              View All Ingredients
            </button>
          </div>
        )}
      </DrillDownModal>

      {/* Movements Drill-Down */}
      <DrillDownModal
        isOpen={movementsModalOpen}
        onClose={() => setMovementsModalOpen(false)}
        title="Today's Movements Details"
      >
        <div className="space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {[
              { label: 'Purchases', key: 'purchases', color: 'emerald' },
              { label: 'Preps', key: 'preps_produced', color: 'blue' },
              { label: 'Transfers In', key: 'transfers_in', color: 'emerald' },
              { label: 'Transfers Out', key: 'transfers_out', color: 'red' },
              { label: 'Adjustments', key: 'adjustments', color: 'amber' },
            ].map(s => (
              <div key={s.key} className={`bg-[#09090b] border border-${s.color}-500/30 rounded-lg p-4 text-center`}>
                <p className="text-xs text-zinc-400 mb-1">{s.label}</p>
                <p className={`text-2xl font-bold font-mono text-${s.color}-500`}>{movements?.summary?.[s.key] || 0}</p>
              </div>
            ))}
          </div>
          <div>
            <h3 className="text-sm font-semibold text-zinc-400 uppercase tracking-wider mb-3">All Movements</h3>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {movements?.movements?.length === 0 ? (
                <p className="text-sm text-zinc-500 text-center py-8">No movements today</p>
              ) : (
                movements?.movements?.map((movement, idx) => (
                  <div key={movement.ledger_id || `mv-${idx}`} className="flex items-center justify-between p-3 bg-[#09090b] border border-[#27272a] rounded-lg hover:border-[#3f3f46] transition-colors">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        movement.direction === 'IN' ? 'bg-emerald-500/10' : 'bg-red-500/10'
                      }`}>
                        {movement.direction === 'IN' ? <ArrowUpRight className="w-4 h-4 text-emerald-500" /> : <ArrowDownRight className="w-4 h-4 text-red-500" />}
                      </div>
                      <div>
                        <p className="text-sm font-medium">{movement.ingredient_name}</p>
                        {movement.occurred_at ? (
                          <p className="text-xs text-zinc-500" data-testid={`movement-occurred-${idx}`}>
                            {movement.movement_type?.replace(/_/g, ' ')} &middot; {formatDateTime(movement.occurred_at)}
                            {movement.synced_at && movement.synced_at !== movement.occurred_at && (
                              <span className="ml-2 text-[10px] text-zinc-600" title={`Synced ${formatDateTime(movement.synced_at)}`}>
                                (synced {formatDateTime(movement.synced_at)})
                              </span>
                            )}
                          </p>
                        ) : (
                          <p className="text-xs text-amber-500/80" data-testid={`movement-occurred-fallback-${idx}`} title="Upstream did not provide a business/event date for this row">
                            {movement.movement_type?.replace(/_/g, ' ')} &middot; {formatDateTime(movement.synced_at || movement.created_at)}
                            <span className="ml-1 text-[10px] text-zinc-500">— occurred date unavailable, showing synced date</span>
                          </p>
                        )}
                      </div>
                    </div>
                    <p className={`text-sm font-mono font-semibold ${movement.direction === 'IN' ? 'text-emerald-500' : 'text-red-500'}`}>
                      {movement.direction === 'IN' ? '+' : '-'}{movement.quantity || movement.qty} {movement.base_unit}
                    </p>
                  </div>
                ))
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <button onClick={() => { setMovementsModalOpen(false); navigate('/ingredients'); }}
              className="py-3 bg-[#27272a] hover:bg-[#3f3f46] rounded-lg font-medium transition-colors">
              View Ingredients
            </button>
            <button onClick={() => { setMovementsModalOpen(false); navigate('/audits'); }}
              className="py-3 bg-blue-600 hover:bg-blue-700 rounded-lg font-medium transition-colors">
              View Audits
            </button>
          </div>
        </div>
      </DrillDownModal>

      {/* Unresolved Sync Queue Modal */}
      <DrillDownModal
        isOpen={unresolvedModalOpen}
        onClose={() => { setUnresolvedModalOpen(false); setExpandedItem(null); setUnresolvedTab('all'); }}
        title="Unresolved Sync Queue"
      >
        {unresolvedLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-yellow-500" />
          </div>
        ) : unresolvedItems.length === 0 ? (
          <p className="text-sm text-zinc-500 text-center py-8" data-testid="unresolved-empty">No unresolved items</p>
        ) : (
          <div className="space-y-4" data-testid="unresolved-list">
            {/* Top Fix Targets */}
            {unresolvedGrouped?.top_fix_targets?.length > 0 && (
              <div className="p-3 bg-red-500/5 border border-red-500/15 rounded-lg" data-testid="top-fix-targets">
                <p className="text-[10px] text-red-400 uppercase font-semibold mb-2">Top Fix Targets (by frequency)</p>
                <div className="flex flex-wrap gap-1.5">
                  {unresolvedGrouped.top_fix_targets.slice(0, 8).map((t, i) => (
                    <span key={i} className="text-[10px] px-2 py-0.5 bg-[#09090b] border border-[#27272a] rounded text-zinc-300">
                      {t.item_name?.slice(0, 30)}{t.item_name?.length > 30 ? '...' : ''} <span className="text-red-400 font-mono">x{t.count}</span>
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Category tabs */}
            <div className="flex items-center gap-1 border-b border-[#27272a] pb-2">
              {[
                { key: 'all', label: 'All', count: unresolvedItems.length },
                { key: 'missing_ingredient', label: 'Missing Ingredient', count: unresolvedGrouped?.groups?.missing_ingredient?.count || 0 },
                { key: 'unit_mismatch', label: 'Unit Mismatch', count: unresolvedGrouped?.groups?.unit_mismatch?.count || 0 },
                { key: 'other', label: 'Other', count: (unresolvedGrouped?.groups?.malformed?.count || 0) + (unresolvedGrouped?.groups?.other?.count || 0) },
              ].map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setUnresolvedTab(tab.key)}
                  className={`text-[10px] px-2 py-1 rounded transition-colors ${
                    unresolvedTab === tab.key
                      ? 'bg-zinc-700 text-zinc-100'
                      : 'text-zinc-500 hover:text-zinc-300'
                  }`}
                  data-testid={`unresolved-tab-${tab.key}`}
                >
                  {tab.label} ({tab.count})
                </button>
              ))}
            </div>

            {/* Guidance banner per tab */}
            {unresolvedTab === 'missing_ingredient' && (
              <p className="text-[10px] text-amber-400/80 bg-amber-500/5 border border-amber-500/10 rounded px-2 py-1.5">
                These items have no ingredient mapping in Expense Capture. Fix upstream: map each bill item to an ingredient in Expense Capture, then re-sync.
              </p>
            )}
            {unresolvedTab === 'unit_mismatch' && (
              <p className="text-[10px] text-amber-400/80 bg-amber-500/5 border border-amber-500/10 rounded px-2 py-1.5">
                These items are mapped but the unit class doesn't match (e.g. pcs vs grams). Fix in Expense Capture (correct unit) or Control Room (update canonical unit).
              </p>
            )}

            {/* Item list */}
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {(unresolvedTab === 'all'
                ? unresolvedItems
                : unresolvedTab === 'missing_ingredient'
                  ? (unresolvedGrouped?.groups?.missing_ingredient?.items || [])
                  : unresolvedTab === 'unit_mismatch'
                    ? (unresolvedGrouped?.groups?.unit_mismatch?.items || [])
                    : [...(unresolvedGrouped?.groups?.malformed?.items || []), ...(unresolvedGrouped?.groups?.other?.items || [])]
              ).map(item => {
                const isExpanded = expandedItem === item.queue_id;
                const ref = item.source_event_ref || {};
                const isLikelyNonConsumable = /discount|packaging|delivery|service charge|gst|tax|charge|tip|round/i.test(ref.item_name || '');
                const isMissingIngredient = item.reason?.includes('No ingredient') || item.dependency_type === 'MISSING_INGREDIENT_ID';
                const isUnitMismatch = item.reason?.includes('Unit class mismatch');
                // Canonical 5-state vocabulary
                const status = item.resolution_status || 'still_unresolved';
                const isSentForMapping = status === 'sent_upstream_for_mapping';
                const isSentForUnitReview = status === 'sent_upstream_for_unit_review';
                const isResolvedOnReplay = status === 'resolved_on_replay';
                const isPermanentlyBlocked = status === 'permanently_blocked';
                const hasPendingUpstreamAction = isSentForMapping || isSentForUnitReview;
                // Pill config — muted, operational, non-celebratory
                const statePill = {
                  still_unresolved: {
                    label: 'open',
                    cls: 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20',
                  },
                  sent_upstream_for_mapping: {
                    label: 'mapping sent upstream',
                    cls: 'bg-amber-500/10 text-amber-400/80 border-amber-500/20',
                  },
                  sent_upstream_for_unit_review: {
                    label: 'unit review sent upstream',
                    cls: 'bg-amber-500/10 text-amber-400/80 border-amber-500/20',
                  },
                  resolved_on_replay: {
                    label: 'resolved on replay',
                    cls: 'bg-emerald-500/10 text-emerald-400/75 border-emerald-500/20',
                  },
                  permanently_blocked: {
                    label: 'blocked',
                    cls: 'bg-red-500/10 text-red-400/80 border-red-500/20',
                  },
                }[status] || { label: status, cls: 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20' };

                return (
                  <div key={item.queue_id} className={`bg-[#09090b] border rounded-lg overflow-hidden ${hasPendingUpstreamAction ? 'border-amber-500/25' : 'border-[#27272a]'}`} data-testid={`unresolved-item-${item.queue_id}`}>
                    {/* Header row */}
                    <div className="p-3">
                      <div className="flex items-start justify-between mb-1.5">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-zinc-200 truncate">{ref.item_name || 'Unknown Item'}</p>
                          <p className="text-[10px] text-zinc-500 mt-0.5">
                            Vendor: {ref.vendor_name || 'N/A'} · Bill: {(ref.bill_id || ref.event_id || 'N/A').slice(0, 12)}
                            {' · '}Source: {SOURCE_LABELS[item.source] || item.source}
                          </p>
                        </div>
                        <div className="flex items-center gap-1.5 ml-2">
                          <span
                            className={`text-[10px] px-1.5 py-0.5 rounded whitespace-nowrap border ${statePill.cls}`}
                            data-testid={`unresolved-state-pill-${item.queue_id}`}
                            title={`resolution_status: ${status}`}
                          >
                            {statePill.label}
                          </span>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded whitespace-nowrap ${
                            item.reason?.includes('Unit class') ? 'bg-orange-500/10 text-orange-400 border border-orange-500/20' :
                            item.reason?.includes('No ingredient') ? 'bg-red-500/10 text-red-400 border border-red-500/20' :
                            'bg-zinc-500/10 text-zinc-400 border border-zinc-500/20'
                          }`}>
                            {item.reason?.includes('Unit class') ? 'UNIT MISMATCH' :
                             item.reason?.includes('No ingredient') ? 'NO MAPPING' : 'OTHER'}
                          </span>
                        </div>
                      </div>
                      <p className="text-[10px] text-zinc-600 mb-2 leading-relaxed">{item.reason}</p>

                      {/* Actions row */}
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] text-zinc-600">First seen: {getTimeSince(item.first_seen_at)}</span>
                        <div className="flex items-center gap-1.5">
                          {isMissingIngredient && !hasPendingUpstreamAction && !isResolvedOnReplay && (
                            <button
                              onClick={() => { setMappingItem(item); setIngredientSearch(''); setIngredientResults([]); setSelectedIngredient(null); }}
                              className="text-[10px] px-2 py-0.5 text-emerald-400 border border-emerald-500/20 rounded hover:bg-emerald-500/10 transition-colors font-medium"
                              data-testid={`map-ingredient-${item.queue_id}`}
                            >
                              Map to Ingredient
                            </button>
                          )}
                          {isUnitMismatch && !isSentForUnitReview && !isResolvedOnReplay && (
                            <button
                              onClick={() => handleFlagUnitIssue(item.queue_id)}
                              className="text-[10px] px-2 py-0.5 text-orange-400 border border-orange-500/20 rounded hover:bg-orange-500/10 transition-colors font-medium"
                              data-testid={`flag-unit-${item.queue_id}`}
                            >
                              Flag Unit Issue
                            </button>
                          )}
                          <button
                            onClick={() => setExpandedItem(isExpanded ? null : item.queue_id)}
                            className="text-[10px] px-2 py-0.5 text-blue-400 border border-blue-500/20 rounded hover:bg-blue-500/10 transition-colors"
                            data-testid={`view-context-${item.queue_id}`}
                          >
                            {isExpanded ? 'Hide' : 'Context'}
                          </button>
                          {isLikelyNonConsumable && (
                            <button
                              onClick={() => handleIgnoreItem(item.queue_id)}
                              className="text-[10px] px-2 py-0.5 text-zinc-400 border border-zinc-500/20 rounded hover:bg-zinc-500/10 transition-colors"
                              data-testid={`ignore-item-${item.queue_id}`}
                            >
                              Non-consumable
                            </button>
                          )}
                          <button
                            onClick={() => handleDismissItem(item.queue_id)}
                            className="text-[10px] px-2 py-0.5 text-zinc-500 border border-zinc-700 rounded hover:bg-zinc-800 transition-colors"
                            data-testid={`dismiss-item-${item.queue_id}`}
                          >
                            Dismiss
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Expanded context */}
                    {isExpanded && (
                      <div className="px-3 pb-3 pt-0 border-t border-[#27272a]">
                        <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-[10px]">
                          <div><span className="text-zinc-600">Item Name:</span> <span className="text-zinc-300">{ref.item_name || '—'}</span></div>
                          <div><span className="text-zinc-600">Bill ID:</span> <span className="text-zinc-300 font-mono">{ref.bill_id || ref.event_id || '—'}</span></div>
                          <div><span className="text-zinc-600">Vendor:</span> <span className="text-zinc-300">{ref.vendor_name || '—'}</span></div>
                          <div><span className="text-zinc-600">Ingredient ID:</span> <span className="text-zinc-300 font-mono">{ref.ingredient_id || 'null'}</span></div>
                          <div><span className="text-zinc-600">Quantity:</span> <span className="text-zinc-300">{ref.quantity ?? '—'} {ref.unit || ''}</span></div>
                          <div><span className="text-zinc-600">Amount:</span> <span className="text-zinc-300">{ref.amount ?? '—'}</span></div>
                          <div><span className="text-zinc-600">Mapping Status:</span> <span className="text-zinc-300">{ref.mapping_status || ref.ingredient_mapping_status || '—'}</span></div>
                          <div><span className="text-zinc-600">Dependency:</span> <span className="text-zinc-300">{item.dependency_type || '—'}</span></div>
                          <div><span className="text-zinc-600">Retry Count:</span> <span className="text-zinc-300">{item.retry_count || 0} / {item.max_retries || 5}</span></div>
                          <div><span className="text-zinc-600">Retry Eligible:</span> <span className={item.retry_eligible ? 'text-amber-400' : 'text-red-400'}>{item.retry_eligible ? 'Yes' : 'No'}</span></div>
                        </div>
                        <p className="text-[10px] text-zinc-600 mt-2 pt-2 border-t border-[#27272a]/50">
                          Fix upstream in Expense Capture or Control Room. This item will automatically apply on the next sync after the root cause is resolved.
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </DrillDownModal>


      {/* Map to Ingredient Modal */}
      <DrillDownModal
        isOpen={!!mappingItem}
        onClose={() => { setMappingItem(null); setSelectedIngredient(null); setIngredientSearch(''); setIngredientResults([]); }}
        title="Map to Ingredient"
      >
        {mappingItem && (
          <div className="space-y-4" data-testid="mapping-modal">
            {/* Context panel */}
            <div className="p-3 bg-[#09090b] border border-[#27272a] rounded-lg">
              <p className="text-[10px] text-zinc-500 uppercase mb-2">Unresolved Item</p>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[10px]">
                <div><span className="text-zinc-600">Item Name:</span> <span className="text-zinc-200 font-medium">{mappingItem.source_event_ref?.item_name || '—'}</span></div>
                <div><span className="text-zinc-600">Vendor:</span> <span className="text-zinc-300">{mappingItem.source_event_ref?.vendor_name || 'N/A'}</span></div>
                <div><span className="text-zinc-600">Bill ID:</span> <span className="text-zinc-300 font-mono">{(mappingItem.source_event_ref?.bill_id || mappingItem.source_event_ref?.event_id || '—').slice(0, 20)}</span></div>
                <div><span className="text-zinc-600">Quantity:</span> <span className="text-zinc-300">{mappingItem.source_event_ref?.quantity ?? '—'} {mappingItem.source_event_ref?.unit || ''}</span></div>
                <div><span className="text-zinc-600">Amount:</span> <span className="text-zinc-300">{mappingItem.source_event_ref?.amount ?? '—'}</span></div>
                <div><span className="text-zinc-600">Reason:</span> <span className="text-red-400">{mappingItem.reason?.slice(0, 50)}</span></div>
              </div>
            </div>

            {/* Ingredient search */}
            <div>
              <p className="text-[10px] text-zinc-500 uppercase mb-1.5">Select Ingredient</p>
              <input
                type="text"
                value={ingredientSearch}
                onChange={e => handleIngredientSearch(e.target.value)}
                placeholder="Search ingredients..."
                className="w-full px-3 py-2 text-sm bg-[#09090b] border border-[#27272a] rounded-lg text-zinc-200 placeholder-zinc-600 focus:border-emerald-500/50 focus:outline-none"
                data-testid="ingredient-search-input"
                autoFocus
              />
              {ingredientResults.length > 0 && (
                <div className="mt-1 max-h-48 overflow-y-auto border border-[#27272a] rounded-lg bg-[#09090b]">
                  {ingredientResults.map(ing => (
                    <button
                      key={ing.ingredient_id}
                      onClick={() => { setSelectedIngredient(ing); setIngredientSearch(ing.ingredient_name); setIngredientResults([]); }}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-[#27272a] transition-colors border-b border-[#27272a]/50 last:border-0 ${
                        selectedIngredient?.ingredient_id === ing.ingredient_id ? 'bg-emerald-500/10 text-emerald-300' : 'text-zinc-300'
                      }`}
                      data-testid={`ingredient-option-${ing.ingredient_id}`}
                    >
                      <span className="font-medium">{ing.ingredient_name}</span>
                      <span className="text-[10px] text-zinc-500 ml-2">{ing.base_uom || ''} · {ing.category || 'uncategorized'}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Confirmation */}
            {selectedIngredient && (
              <div className="p-3 bg-emerald-500/5 border border-emerald-500/20 rounded-lg" data-testid="mapping-confirmation">
                <p className="text-[10px] text-emerald-400 uppercase font-semibold mb-2">Confirm Mapping</p>
                <p className="text-sm text-zinc-200">
                  <span className="text-zinc-400">"{mappingItem.source_event_ref?.item_name}"</span>
                  {' → '}
                  <span className="text-emerald-400 font-medium">{selectedIngredient.ingredient_name}</span>
                  <span className="text-[10px] text-zinc-500 ml-1">({selectedIngredient.base_uom || 'no unit'})</span>
                </p>
                <div className="mt-2 text-[10px] text-zinc-500 space-y-0.5">
                  <p>This will save the mapping in Expense Capture (canonical source).</p>
                  <p>The item will apply to inventory on the next sync cycle.</p>
                </div>
                <div className="flex items-center gap-2 mt-3">
                  <button
                    onClick={handleSubmitMapping}
                    disabled={mappingSubmitting}
                    className="px-4 py-1.5 text-xs font-medium bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded transition-colors"
                    data-testid="confirm-mapping-btn"
                  >
                    {mappingSubmitting ? 'Sending...' : 'Confirm & Send to Expense Capture'}
                  </button>
                  <button
                    onClick={() => { setMappingItem(null); setSelectedIngredient(null); }}
                    className="px-4 py-1.5 text-xs text-zinc-400 border border-zinc-700 rounded hover:bg-zinc-800 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </DrillDownModal>


      {/* Sales Capture Unresolved Modal */}
      <DrillDownModal
        isOpen={salesCaptureUnresolvedOpen}
        onClose={() => setSalesCaptureUnresolvedOpen(false)}
        title="Sales Capture — Unresolved Items"
      >
        {salesCaptureUnresolved.length === 0 ? (
          <p className="text-sm text-zinc-500 text-center py-8" data-testid="sc-unresolved-empty">No unresolved sold items</p>
        ) : (
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {salesCaptureUnresolved.map((item, i) => (
              <div key={item.unresolved_id || `sc-unresolved-${i}`} className="p-3 bg-[#09090b] border border-[#27272a] rounded-lg" data-testid={`sc-unresolved-item-${i}`}>
                <div className="flex items-center justify-between mb-1">
                  <p className="text-sm font-medium text-zinc-200">{item.item_name}</p>
                  <span className="text-[10px] font-mono text-amber-400 bg-amber-500/10 px-1.5 py-0.5 rounded">{item.reason}</span>
                </div>
                <div className="flex items-center gap-4 text-[10px] text-zinc-500">
                  <span>Date: {item.business_date}</span>
                  <span>Qty: {item.quantity_sold}</span>
                  {item.channel && <span>Channel: {item.channel}</span>}
                  {item.source_record_id && <span className="truncate max-w-[120px]">Ref: {item.source_record_id}</span>}
                </div>
              </div>
            ))}
          </div>
        )}
      </DrillDownModal>

      {/* Sales Capture Sync Runs Modal */}
      <DrillDownModal
        isOpen={salesCaptureRunsOpen}
        onClose={() => setSalesCaptureRunsOpen(false)}
        title="Sales Capture — Sync History"
      >
        {salesCaptureSyncRuns.length === 0 ? (
          <p className="text-sm text-zinc-500 text-center py-8">No sync runs yet</p>
        ) : (
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {salesCaptureSyncRuns.map((run, i) => (
              <div key={run.sync_run_id || `sc-run-${i}`} className="p-3 bg-[#09090b] border border-[#27272a] rounded-lg" data-testid={`sc-run-${i}`}>
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <span className={`px-1.5 py-0.5 text-[10px] font-mono font-medium rounded ${
                      run.sync_health === 'HEALTHY' ? 'bg-emerald-500/10 text-emerald-400' :
                      run.sync_health === 'PARTIAL' ? 'bg-amber-500/10 text-amber-400' :
                      'bg-red-500/10 text-red-400'
                    }`}>{run.sync_health}</span>
                    <span className="text-xs text-zinc-400">{run.start_date} → {run.end_date}</span>
                  </div>
                  <span className="text-[10px] text-zinc-500">{run.duration_ms}ms</span>
                </div>
                <div className="flex items-center gap-3 text-[10px] text-zinc-500 mt-1">
                  <span>Fetched: {run.fetched_rows}</span>
                  <span className="text-emerald-400">Mapped: {run.mapped_rows}</span>
                  <span className="text-amber-400">Unresolved: {run.unresolved_rows}</span>
                  <span className="text-blue-400">Depletion: {run.depletion_events_written}</span>
                  {run.recomputed_events > 0 && <span className="text-orange-400">Recomputed: {run.recomputed_events}</span>}
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-[10px] text-zinc-600">{run.confidence?.replace('_', ' ')}</span>
                  <span className="text-[10px] text-zinc-600">Mode: {run.sync_mode}</span>
                  {run.mapping_coverage !== undefined && (
                    <span className={`text-[10px] font-mono ${
                      run.mapping_coverage >= (run.coverage_threshold || 85) ? 'text-emerald-500' : 'text-red-400'
                    }`}>
                      Coverage: {run.mapping_coverage?.toFixed(1)}%
                    </span>
                  )}
                  {run.activation_blocked && <span className="text-[10px] text-red-400 font-mono">BLOCKED</span>}
                  <span className="text-[10px] text-zinc-600">{formatDateTime(run.started_at)}</span>
                </div>
                {run.error_summary && (
                  <p className="text-[10px] text-red-400/80 mt-1 truncate">{run.error_summary}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </DrillDownModal>

      {/* Sales Capture Unmapped Impact Modal */}
      <DrillDownModal
        isOpen={salesCaptureUnmappedOpen}
        onClose={() => setSalesCaptureUnmappedOpen(false)}
        title="Sales Capture — Top Unmapped by Impact"
      >
        {salesCaptureUnmappedImpact.length === 0 ? (
          <p className="text-sm text-zinc-500 text-center py-8" data-testid="sc-unmapped-empty">No unmapped items found</p>
        ) : (
          <div className="space-y-3" data-testid="sc-unmapped-impact-list">
            <p className="text-[10px] text-zinc-500">Sorted by total quantity sold. Fix these first to raise mapping coverage.</p>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-[10px] text-zinc-500 uppercase border-b border-[#27272a]">
                    <th className="text-left py-2 pr-3">Item Name</th>
                    <th className="text-right py-2 px-2">Qty Sold</th>
                    <th className="text-right py-2 px-2">Occurrences</th>
                    <th className="text-left py-2 pl-2">Last Seen</th>
                  </tr>
                </thead>
                <tbody>
                  {salesCaptureUnmappedImpact.map((item, i) => (
                    <tr key={item.item_name || `unmapped-${i}`} className="border-b border-[#27272a]/50 hover:bg-[#27272a]/30" data-testid={`sc-unmapped-row-${i}`}>
                      <td className="py-2 pr-3">
                        <p className="font-medium text-zinc-200 truncate max-w-[180px]">{item.item_name}</p>
                        {item.channels?.filter(Boolean).length > 0 && (
                          <p className="text-[10px] text-zinc-600">{item.channels.filter(Boolean).join(', ')}</p>
                        )}
                      </td>
                      <td className="text-right py-2 px-2 font-mono font-bold text-red-400">{item.total_qty_sold?.toFixed(1)}</td>
                      <td className="text-right py-2 px-2 font-mono text-zinc-400">{item.occurrence_count}</td>
                      <td className="py-2 pl-2 text-zinc-500">{item.last_seen ? formatDateTime(item.last_seen) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </DrillDownModal>

      <MissingCostDrawer
        open={missingCostDrawerOpen}
        onOpenChange={setMissingCostDrawerOpen}
        outletId={selectedOutlet}
      />
    </div>
  );
};

export default Dashboard;
